import type { CmmdTaskStatus } from "@cmmd-forge/contracts";
import { Trash2Icon } from "lucide-react";
import { cn } from "~/lib/utils";
import { WorkItemProjectIcon } from "./WorkItemProjectIcon";
import type { WorkItem } from "../homebase/unifiedWorkItems";
import { formatTaskDisplayId } from "./displayHelpers";
import { buildTaskMetadataSegments, formatPriorityToken } from "./taskRowFormatting";
import { getStatusConfig, STATUS_CONFIGS } from "./statusConfig";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";

interface TaskRowProps {
  item: WorkItem;
  isSelected: boolean;
  onSelect: (id: string) => void;
  isChecked?: boolean;
  onToggleChecked?: (id: string, checked: boolean) => void;
  onUpdateStatus?: (id: string, status: CmmdTaskStatus) => void;
  onDeleteTask?: (id: string) => void;
}

export function AgentBadge({
  agentStatus,
  agentProvider,
}: Pick<WorkItem, "agentStatus" | "agentProvider">) {
  if (agentStatus === "none" || agentStatus === "idle") return null;
  return (
    <span
      title="Agent running"
      className="flex items-center gap-1 rounded border border-success-border bg-success-surface px-1.5 py-0.5 text-[11px] font-medium text-success-foreground"
    >
      <span className="inline-block size-1.5 rounded-full bg-success motion-safe:animate-status-pulse" />
      {agentProvider ?? "Agent"}
    </span>
  );
}

function StatusCircle({ status }: { status: string | null }) {
  const { circleClass } = getStatusConfig(status);
  return (
    <span
      className={cn(
        "flex size-[14px] shrink-0 items-center justify-center rounded-full border-[1.5px]",
        circleClass,
      )}
    />
  );
}

function PriorityIndicator({ priority }: { priority: string | null }) {
  const normalizedPriority = priority?.trim();
  if (!normalizedPriority) return null;
  const token = formatPriorityToken(priority) ?? { label: "P?", tone: "neutral" as const };
  return (
    <span
      title={`${token.label} priority`}
      className={cn(
        "inline-flex items-center rounded px-1.5 py-px font-mono text-[11px] tabular-nums",
        token.tone === "critical"
          ? "border border-warning-border bg-warning-surface text-warning-foreground"
          : "border border-border/60 bg-background/30 text-muted-foreground/70",
      )}
    >
      <span>{token.label}</span>
    </span>
  );
}

function SourcePill({ item }: { item: WorkItem }) {
  const label =
    item.source === "cmmd-campaign"
      ? "Campaign"
      : item.source === "cmmd-project"
        ? "Project"
        : item.workspaceName;
  if (!label) return null;
  return (
    <span className="inline-flex max-w-[148px] shrink-0 items-center truncate rounded border border-border/60 bg-background/30 px-1.5 py-px text-[11px] font-medium text-muted-foreground/75">
      {label}
    </span>
  );
}

function BlockedByPill({ count }: { count: number }) {
  if (count <= 0) return null;
  // The count is authoritative — it's the number of deps whose status is not
  // 'done' as computed in the viewmodel. Uses a subdued red palette rather
  // than amber (reserved for priority) or green (reserved for ready/agent).
  return (
    <span
      title={`Blocked by ${count} upstream task${count === 1 ? "" : "s"}`}
      aria-label={`Blocked by ${count}`}
      className="inline-flex items-center gap-0.5 rounded border border-destructive-border bg-destructive-surface px-1 py-px text-[11px] font-medium text-destructive-foreground"
    >
      <span aria-hidden className="leading-none">
        ⬣
      </span>
      <span>Blocked by {count}</span>
    </span>
  );
}

function formatStatusLabel(status: string | null): string {
  if (!status) return "Todo";
  const configured = STATUS_CONFIGS.find((statusConfig) => statusConfig.status === status);
  if (configured) return configured.label;
  return status
    .split("_")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

export function TaskRow({
  item,
  isSelected,
  onSelect,
  isChecked = false,
  onToggleChecked,
  onUpdateStatus,
  onDeleteTask,
}: TaskRowProps) {
  const displayId = formatTaskDisplayId(item.id);
  // CMMD workflow mutations require an exact workflow stage id. Command Center
  // does not load that graph, so its category-only control is safe only for
  // local manual tasks.
  const canUpdateStatus = item.id.startsWith("manual:");
  const statusValue = typeof item.status === "string" ? item.status : "todo";
  const statusLabel = formatStatusLabel(item.status);
  const shouldShowCheckbox = isChecked || isSelected;
  const canDelete = item.id.startsWith("manual:") && onDeleteTask;
  const metadataSegments = buildTaskMetadataSegments({
    id: displayId,
    projectName: item.projectName,
    campaignName: item.campaignName,
    teamName: item.teamName,
  });

  const statusControl =
    canUpdateStatus && onUpdateStatus ? (
      <Select
        value={statusValue}
        onValueChange={(next) => {
          if (next !== null) {
            onUpdateStatus(item.id, next as CmmdTaskStatus);
          }
        }}
        items={STATUS_CONFIGS.map((statusConfig) => ({
          value: statusConfig.status,
          label: statusConfig.label,
        }))}
      >
        <SelectTrigger
          size="xs"
          className={cn(
            "inline-flex h-7 w-full min-w-0 whitespace-nowrap rounded-md border-border/35 bg-background/20 px-2",
            "text-xs font-medium text-muted-foreground/90 hover:text-foreground",
          )}
          aria-label={`Update status for ${item.title}`}
          title={statusLabel}
        >
          <SelectValue className="truncate text-xs font-medium">{statusLabel}</SelectValue>
        </SelectTrigger>
        <SelectPopup align="end" alignItemWithTrigger={false}>
          {STATUS_CONFIGS.map((statusConfig) => (
            <SelectItem key={statusConfig.status} value={statusConfig.status} hideIndicator>
              {statusConfig.label}
            </SelectItem>
          ))}
        </SelectPopup>
      </Select>
    ) : null;
  return (
    <div
      role="listitem"
      data-selected={isSelected}
      className={cn(
        "group grid cursor-pointer grid-cols-[auto_auto_minmax(0,1fr)_auto_auto] items-start gap-x-3 rounded-md border border-transparent px-3 py-2.5 transition-[background-color,border-color]",
        "hover:bg-muted/35",
        isSelected && "bg-muted/45",
      )}
      onClick={() => onSelect(item.id)}
    >
      <div
        className="row-start-1 flex h-7 shrink-0 items-center"
        role="gridcell"
        data-testid={`task-row-checkbox-cell-${item.id}`}
      >
        <input
          type="checkbox"
          checked={isChecked}
          aria-label={`Select ${item.title}`}
          className={cn(
            "size-3.5 rounded-[3px] border border-border/75 accent-primary transition-opacity",
            shouldShowCheckbox
              ? "pointer-events-auto opacity-100"
              : "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100",
          )}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => onToggleChecked?.(item.id, event.currentTarget.checked)}
        />
      </div>

      <div
        className="row-start-1 flex h-7 shrink-0 items-center"
        role="gridcell"
        data-testid={`task-row-status-cell-${item.id}`}
      >
        <StatusCircle status={item.status} />
      </div>

      <div
        className="row-start-1 flex min-h-7 min-w-0 items-center gap-1.5"
        role="gridcell"
        data-testid={`task-row-title-cell-${item.id}`}
      >
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-[17px] leading-tight font-semibold",
            item.status === "done" ? "text-muted-foreground/40 line-through" : "text-foreground",
          )}
        >
          {item.title}
        </span>
        <SourcePill item={item} />
        <BlockedByPill count={item.blockedByCount} />
        <AgentBadge agentStatus={item.agentStatus} agentProvider={item.agentProvider} />
      </div>

      <div className="row-start-1 flex h-7 items-center justify-end" role="gridcell">
        <PriorityIndicator priority={item.priority} />
      </div>

      <div
        data-testid={`task-row-status-control-wrapper-${item.id}`}
        className="row-start-1 flex h-7 w-[128px] items-center justify-end"
        role="gridcell"
        onClick={(event) => event.stopPropagation()}
      >
        {statusControl ?? (
          <span
            className="inline-flex h-7 w-full items-center rounded-md px-2 text-xs font-medium text-muted-foreground/62"
            data-testid={`task-row-status-control-${item.id}`}
          >
            {statusLabel}
          </span>
        )}
      </div>

      <div className="col-start-3 col-span-3 row-start-2 mt-1 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground/65">
        {metadataSegments.map((segment, index) => (
          <span
            className="flex min-w-0 items-center gap-1.5"
            key={`${item.id}:${segment.kind}:${segment.label}`}
          >
            {index > 0 ? <span aria-hidden>·</span> : null}
            {/* The project reads as a project here, exactly as it does in the
                sidebar and the palette — a bare string was the odd one out. */}
            {segment.kind === "project" ? (
              <WorkItemProjectIcon
                projectName={segment.label}
                projectIcon={item.projectIcon}
                projectIconUrl={item.projectIconUrl}
              />
            ) : null}
            <span
              className={cn(
                "truncate",
                segment.kind === "id" &&
                  "font-mono text-[11px] text-muted-foreground/50 tabular-nums",
              )}
            >
              {segment.label}
            </span>
          </span>
        ))}
        {canDelete ? (
          <button
            type="button"
            aria-label={`Delete ${item.title}`}
            className="ml-auto inline-flex h-5 shrink-0 items-center gap-1 rounded-sm px-1.5 text-[11px] text-muted-foreground/55 opacity-0 transition-colors hover:bg-destructive-surface hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={(event) => {
              event.stopPropagation();
              onDeleteTask(item.id);
            }}
          >
            <Trash2Icon className="size-3" />
            Delete
          </button>
        ) : null}
      </div>
    </div>
  );
}
